import { useEffect, useRef } from 'react';
import {
  isLiveGameSyncConfigured,
  releaseLiveGameSyncForceListen,
  resumeLiveGameSync,
  retainLiveGameSyncForceListen,
  subscribeLiveGameEvents,
  syncLiveGameSyncConnection
} from '../services/liveGameSync';

/**
 * Subscribe this page to live game events and keep the shared AppSync socket
 * in sync with live need (session game caches + optional forceListen).
 * Socket is not closed on route changes or tab hide — only when nothing is live.
 * On tab show: refetch the mounted page. On unexpected socket drop: refetch
 * all Home/Picks/Standings session caches, then the mounted page, then reconnect.
 *
 * @param {{
 *   games: Array<Record<string, unknown>>,
 *   onLiveEvent: (event: object) => void,
 *   onVisibilityResume: () => void | Promise<void>,
 *   enabled?: boolean,
 *   forceListen?: boolean,
 *   onSubscribed?: () => void | Promise<void>
 * }} args
 */
export function useLiveGameSync({
  games,
  onLiveEvent,
  onVisibilityResume,
  enabled = true,
  forceListen = false,
  onSubscribed
}) {
  const onLiveEventRef = useRef(onLiveEvent);
  const onResumeRef = useRef(onVisibilityResume);
  const onSubscribedRef = useRef(onSubscribed);
  const forceListenRef = useRef(forceListen);
  const forceTokenRef = useRef(null);
  const reconciledForListenRef = useRef(false);
  onLiveEventRef.current = onLiveEvent;
  onResumeRef.current = onVisibilityResume;
  onSubscribedRef.current = onSubscribed;
  forceListenRef.current = forceListen;

  useEffect(() => {
    if (!enabled) {
      reconciledForListenRef.current = false;
    }
  }, [enabled]);

  // Refetch on tab show and before unexpected socket reconnect.
  useEffect(() => {
    if (!enabled) return undefined;

    const runResumeFetch = async () => {
      try {
        await onResumeRef.current?.();
      } catch (err) {
        console.warn('liveGameSync resume refetch failed', err);
      }
    };

    const onVisibility = async () => {
      if (document.visibilityState === 'hidden') return;
      await runResumeFetch();
      if (isLiveGameSyncConfigured()) {
        syncLiveGameSyncConnection().catch(() => {});
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    const unsubResume = isLiveGameSyncConfigured()
      ? resumeLiveGameSync(runResumeFetch)
      : () => {};

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      unsubResume();
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !isLiveGameSyncConfigured()) return undefined;

    let cancelled = false;
    const forceToken = {};
    forceTokenRef.current = forceToken;

    const applyForceListen = () => {
      if (forceListenRef.current) {
        retainLiveGameSyncForceListen(forceToken);
      } else {
        releaseLiveGameSyncForceListen(forceToken);
      }
    };

    const sync = async ({ runSubscribedHook = false } = {}) => {
      if (cancelled) return;
      applyForceListen();
      try {
        await syncLiveGameSyncConnection();
        if (runSubscribedHook && !cancelled && !reconciledForListenRef.current) {
          reconciledForListenRef.current = true;
          await onSubscribedRef.current?.();
        }
      } catch (err) {
        console.warn('liveGameSync sync failed', err);
      }
    };

    const unsub = subscribeLiveGameEvents({
      next: (event) => onLiveEventRef.current?.(event),
      error: (err) => console.warn('liveGameSync error', err)
    });

    sync({ runSubscribedHook: true });

    return () => {
      cancelled = true;
      releaseLiveGameSyncForceListen(forceToken);
      if (forceTokenRef.current === forceToken) {
        forceTokenRef.current = null;
      }
      unsub();
      syncLiveGameSyncConnection().catch(() => {});
    };
  }, [enabled]);

  // Re-evaluate when games list / forceListen changes (caches updated by the page).
  useEffect(() => {
    if (!enabled || !isLiveGameSyncConfigured()) return;
    const token = forceTokenRef.current;
    if (token) {
      if (forceListen) retainLiveGameSyncForceListen(token);
      else releaseLiveGameSyncForceListen(token);
    }
    syncLiveGameSyncConnection().catch(() => {});
  }, [enabled, games, forceListen]);
}
